import React from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  Typography,
  Skeleton,
  Button,
} from '@mui/material';
import { UsersRound, SearchX, CircleAlert } from 'lucide-react';
import { ClienteListItem } from '../types';
import { ClienteRowCard } from './ClienteRowCard';

interface Props {
  clientes: ClienteListItem[];
  total: number;
  pagina: number;
  limite: number;
  loading: boolean;
  error?: string | null;
  onEditar: (cliente: ClienteListItem) => void;
  onEliminar: (cliente: ClienteListItem) => void;
  onCambiarPagina: (pagina: number) => void;
  onCambiarLimite: (limite: number) => void;
  busquedaActiva?: boolean;
  onRecargar?: () => void;
  onCrearCliente?: () => void;
}

const HEADER_COLS = [
  { label: 'Cliente', width: '25%' },
  { label: 'Actividad', width: '19%' },
  { label: 'Ubicacion', width: '18%' },
  { label: 'Perfil tributario', width: '12%' },
  { label: 'Contactos', width: '10%' },
  { label: 'Estado', width: '8%' },
  { label: 'Acciones', width: '8%' },
];

function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  actionLabel: string;
  onAction?: () => void;
}) {
  return (
    <TableRow>
      <TableCell colSpan={7} sx={{ border: 'none', py: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ color: '#A0A0A8' }}>{icon}</Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#3E353F' }}>{title}</Typography>
          <Typography sx={{ fontSize: 13, color: '#756D77', textAlign: 'center', maxWidth: 320 }}>
            {description}
          </Typography>
          {onAction && (
            <Button
              variant="outlined"
              size="small"
              onClick={onAction}
              sx={{ mt: 0.5, borderColor: '#D9AACA', color: '#9E427F', borderRadius: '8px' }}
            >
              {actionLabel}
            </Button>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}

export function ClientesTable({
  clientes,
  total,
  pagina,
  limite,
  loading,
  error,
  onEditar,
  onEliminar,
  onCambiarPagina,
  onCambiarLimite,
  busquedaActiva,
  onRecargar,
  onCrearCliente,
}: Props) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
        <Table
          data-testid="clientes-table"
          sx={{ borderCollapse: 'separate', borderSpacing: '0 10px', width: '100%' }}
        >
          <TableHead>
            <TableRow
              sx={{
                position: 'sticky',
                top: 0,
                background: '#F6F4F6',
                zIndex: 1,
                '& th': { border: 'none' },
              }}
            >
              {HEADER_COLS.map((col) => (
                <TableCell
                  key={col.label}
                  sx={{
                    width: col.width,
                    height: 42,
                    fontSize: 18,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.02em',
                    color: '#756D77',
                    px: '18px',
                    py: 0,
                    background: '#F6F4F6',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i} sx={{ '& td': { border: 'none', py: 0.5 } }}>
                  {HEADER_COLS.map((col) => (
                    <TableCell key={col.label}>
                      <Skeleton variant="rectangular" height={76} sx={{ borderRadius: 1 }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : error ? (
              <EmptyState
                icon={<CircleAlert size={44} />}
                title="No fue posible cargar los clientes"
                description="Revisa tu conexion e intenta nuevamente."
                actionLabel="Reintentar"
                onAction={onRecargar}
              />
            ) : clientes.length === 0 && busquedaActiva ? (
              <EmptyState
                icon={<SearchX size={44} />}
                title="No encontramos clientes"
                description="Prueba con otra razon social o NIT."
                actionLabel="Limpiar busqueda"
                onAction={onRecargar}
              />
            ) : clientes.length === 0 ? (
              <EmptyState
                icon={<UsersRound size={44} />}
                title="Aun no tienes clientes"
                description="Crea el primer cliente para comenzar a preparar cotizaciones."
                actionLabel="Crear cliente"
                onAction={onCrearCliente}
              />
            ) : (
              clientes.map((c) => (
                <ClienteRowCard
                  key={c.id}
                  cliente={c}
                  onEditar={onEditar}
                  onEliminar={onEliminar}
                />
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      {!loading && !error && clientes.length > 0 && (
        <Box
          sx={{
            background: '#FFFFFF',
            border: '1px solid #E3DEE4',
            borderRadius: '8px',
            boxShadow: '0 3px 9px rgba(45,30,46,0.06)',
            px: '14px',
            py: '10px',
          }}
        >
          <TablePagination
            component="div"
            count={total}
            page={pagina - 1}
            rowsPerPage={limite}
            rowsPerPageOptions={[10, 20, 50]}
            onPageChange={(_, p) => onCambiarPagina(p + 1)}
            onRowsPerPageChange={(e) => onCambiarLimite(Number(e.target.value))}
            labelRowsPerPage="Por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            sx={{ border: 'none', p: 0 }}
          />
        </Box>
      )}
    </Box>
  );
}
